import React from 'react';
import i18n from '../../../../i18n';
import BankAccountCardSkeleton from './components/BankAccountCardSkeleton';
import BankAccountsEmpty from './components/BankAccountsEmpty';
import PaymentRoutingSettings from './components/PaymentRoutingSettings';
import SmartRoutingPreview from './components/SmartRoutingPreview';
import {
  Landmark,
  Star,
  Pencil,
  Trash2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  PauseCircle,
  PlayCircle,
  Plus,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { ConfirmModal } from '../../../common/ConfirmModal';
import { getLoggedUser } from '../../../../state/user';
import {
  getPaymentAccount,
  updatePaymentAccount,
  actionPaymentAccount,
  getPaymentAccountConfig,
  updatePaymentAccountConfig,
} from './services/bankAccountServices';
import PaymentBankAccountModal from './components/PaymentBankAccountModal';
import '../../../../styles/components/bank-account-cards.scss';
import { NavLink } from 'react-router-dom';

const user = getLoggedUser();

// NOTE: this assumes each account row carries a `verification_status`
// field ('verified' | 'pending') in addition to the existing `is_active` /
// `is_default` flags. Adjust the key below if your API names it differently.

class ClientBankAccount extends React.Component {
  constructor() {
    super();
    this.state = {
      accounts: [],
      isLoading: false,

      // create / update modal
      showFormModal: false,
      selectedAccount: null,

      // set-as-primary confirm
      showPrimaryConfirm: false,
      primaryTarget: null,
      primaryLoading: false,

      // activate / deactivate confirm
      showActionConfirm: false,
      actionTarget: null,
      actionLoading: false,

      hideSidebar: false,

      // payment routing — draft values the user is currently editing
      enrollmentSource: 'mzadcom',
      enrollmentAccountId: null,
      paymentDestination: 'mzadcom',
      paymentAccountId: null,

      // last-saved snapshot, used to detect unsaved changes
      savedRoutingConfig: null,

      auctionEnrollmentConfig: null,
      auctionPaymentConfig: null,
      isConfigLoading: false,
      isRoutingSaving: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.fetchAccounts();
    this.fetchPaymentAccountConfig();
  }

  fetchAccounts = async () => {
    this.setState({ isLoading: true });

    try {
      const response = await getPaymentAccount();

      if (response.success) {
        this.setState({ accounts: response.data || [] });
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  /* ----------------------------------------------------------------------
   * Payment routing config — GET /payment-account/config
   * Returns the organization's saved Auction Enrollment / Auction Payment
   * configuration. Each config carries its own `payment_destination`
   * ('mzadcom' | 'organization') plus an `organization_bank_account` relation —
   * note that relation is populated even for the 'mzadcom' destination
   * (it points at Mzadcom's own settlement account), so the destination
   * must be read from `payment_destination`, not inferred from whether
   * the relation/id is present.
   * -------------------------------------------------------------------- */

  fetchPaymentAccountConfig = async () => {
    this.setState({ isConfigLoading: true });

    try {
      const response = await getPaymentAccountConfig();

      if (response.success) {
        const {
          auction_enrollment: auctionEnrollmentConfig,
          auction_payment: auctionPaymentConfig,
        } = response.data || {};

        const enrollmentSource =
          auctionEnrollmentConfig?.payment_destination === 'mzadcom' ? 'mzadcom' : 'organization';
        const paymentDestination =
          auctionPaymentConfig?.payment_destination === 'mzadcom' ? 'mzadcom' : 'organization';

        // Only treat the id as a selectable destination account when the
        // saved destination is actually "client" — for "mzadcom" the id
        // still points at a (Mzadcom-owned) bank account, not one of the
        // organization's own accounts.
        const enrollmentAccountId =
          enrollmentSource === 'organization'
            ? (auctionEnrollmentConfig?.organization_bank_account_id ?? null)
            : null;
        const paymentAccountId =
          paymentDestination === 'organization'
            ? (auctionPaymentConfig?.organization_bank_account_id ?? null)
            : null;

        const savedRoutingConfig = {
          enrollmentSource,
          enrollmentAccountId,
          paymentDestination,
          paymentAccountId,
        };

        this.setState({
          auctionEnrollmentConfig: auctionEnrollmentConfig || null,
          auctionPaymentConfig: auctionPaymentConfig || null,
          enrollmentSource,
          enrollmentAccountId,
          paymentDestination,
          paymentAccountId,
          savedRoutingConfig,
        });
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ isConfigLoading: false });
    }
  };

  /* ----------------------------------------------------------------------
   * Create / Update — both handled by PaymentBankAccountModal
   * -------------------------------------------------------------------- */

  handleCreate = () => {
    this.setState({ selectedAccount: null, showFormModal: true });
  };

  handleEdit = (row) => {
    this.setState({ selectedAccount: row, showFormModal: true });
  };

  handleFormModalClose = () => {
    this.setState({ showFormModal: false, selectedAccount: null });
  };

  handleSaved = () => {
    this.setState({ showFormModal: false, selectedAccount: null });
    this.fetchAccounts();
  };

  /* ----------------------------------------------------------------------
   * Set as primary — reuses the update endpoint, sending the row's
   * existing fields with is_default forced to true.
   * -------------------------------------------------------------------- */

  handleSetPrimaryClick = (row) => {
    this.setState({ primaryTarget: row, showPrimaryConfirm: true });
  };

  handleSetPrimaryConfirm = async () => {
    const { primaryTarget } = this.state;
    if (!primaryTarget) return;

    this.setState({ primaryLoading: true });

    try {
      const payload = {
        bank_name: primaryTarget.bank_name,
        branch_name: primaryTarget.branch_name,
        account_name: primaryTarget.account_name,
        account_number: primaryTarget.account_number,
        iban: primaryTarget.iban,
        swift_code: primaryTarget.swift_code,
        currency: primaryTarget.currency,
        remarks: primaryTarget.remarks,
        is_default: true,
      };

      const response = await updatePaymentAccount(primaryTarget.id, payload);

      if (response.success) {
        toast.success(response.message || i18n.t('primary_account_updated'));
        this.setState({ showPrimaryConfirm: false, primaryTarget: null });
        this.fetchAccounts();
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ primaryLoading: false });
    }
  };

  /* ----------------------------------------------------------------------
   * Activate / deactivate
   * -------------------------------------------------------------------- */

  handleActionClick = (row) => {
    this.setState({ actionTarget: row, showActionConfirm: true });
  };

  handleActionConfirm = async () => {
    const { actionTarget } = this.state;
    if (!actionTarget) return;

    const action = actionTarget.is_active ? 'deactivate' : 'activate';

    this.setState({ actionLoading: true });

    try {
      const response = await actionPaymentAccount(actionTarget.id, action);

      if (response.success) {
        toast.success(response.message || i18n.t(`account_${action}d`));
        this.setState({ showActionConfirm: false, actionTarget: null });
        this.fetchAccounts();
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ actionLoading: false });
    }
  };

  /* ----------------------------------------------------------------------
   * Payment routing — Auction Enrollment / Auction Payment
   * -------------------------------------------------------------------- */

  handleEnrollmentSourceChange = (source) => {
    this.setState((prevState) => {
      if (source !== 'organization' || prevState.enrollmentAccountId) {
        return { enrollmentSource: source };
      }

      const primaryAccount = prevState.accounts.find((account) => account.is_default);
      return {
        enrollmentSource: source,
        enrollmentAccountId: primaryAccount ? primaryAccount.id : null,
      };
    });
  };

  handleEnrollmentAccountChange = (accountId) => {
    this.setState({ enrollmentAccountId: accountId });
  };

  handlePaymentDestinationChange = (destination) => {
    this.setState((prevState) => {
      if (destination !== 'organization' || prevState.paymentAccountId) {
        return { paymentDestination: destination };
      }

      const primaryAccount = prevState.accounts.find((account) => account.is_default);
      return {
        paymentDestination: destination,
        paymentAccountId: primaryAccount ? primaryAccount.id : null,
      };
    });
  };

  handlePaymentAccountChange = (accountId) => {
    this.setState({ paymentAccountId: accountId });
  };

  isRoutingDirty = () => {
    const {
      savedRoutingConfig,
      enrollmentSource,
      enrollmentAccountId,
      paymentDestination,
      paymentAccountId,
    } = this.state;

    if (!savedRoutingConfig) return false;

    return (
      savedRoutingConfig.enrollmentSource !== enrollmentSource ||
      savedRoutingConfig.enrollmentAccountId !== enrollmentAccountId ||
      savedRoutingConfig.paymentDestination !== paymentDestination ||
      savedRoutingConfig.paymentAccountId !== paymentAccountId
    );
  };

  handleDiscardRoutingChanges = () => {
    const { savedRoutingConfig } = this.state;
    if (!savedRoutingConfig) return;

    this.setState({ ...savedRoutingConfig });
  };

  handleSaveRoutingChanges = async () => {
    const {
      auctionEnrollmentConfig,
      auctionPaymentConfig,
      enrollmentSource,
      enrollmentAccountId,
      paymentDestination,
      paymentAccountId,
    } = this.state;

    if (!auctionEnrollmentConfig?.id || !auctionPaymentConfig?.id) {
      toast.error(i18n.t('something_went_wrong'));
      return;
    }

    this.setState({ isRoutingSaving: true });

    try {
      const [enrollmentResponse, paymentResponse] = await Promise.all([
        updatePaymentAccountConfig(auctionEnrollmentConfig.id, {
          payment_destination: enrollmentSource,
          organization_bank_account_id:
            enrollmentSource === 'organization' ? enrollmentAccountId : null,
        }),
        updatePaymentAccountConfig(auctionPaymentConfig.id, {
          payment_destination: paymentDestination,
          organization_bank_account_id:
            paymentDestination === 'organization' ? paymentAccountId : null,
        }),
      ]);

      if (enrollmentResponse.success && paymentResponse.success) {
        toast.success(i18n.t('routing_settings_updated'));
        // Pull the freshly saved configuration (with updated relations)
        // back down so the preview reflects exactly what the server has.
        await this.fetchPaymentAccountConfig();
      } else {
        toast.error(
          enrollmentResponse.message || paymentResponse.message || i18n.t('something_went_wrong')
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ isRoutingSaving: false });
    }
  };

  maskNumber = (value) => {
    if (!value) return '--';
    const str = String(value);
    return str.length > 4 ? `****${str.slice(-4)}` : str;
  };

  renderVerificationBadge = (row) => {
    const isVerified = row.verification_status
      ? row.verification_status === 'verified'
      : Boolean(row.is_verified);

    return isVerified ? (
      <span className="bank-account-card__status bank-account-card__status--verified">
        <CheckCircle2 size={14} />
        {i18n.t('verified')}
      </span>
    ) : (
      <span className="bank-account-card__status bank-account-card__status--pending">
        <Clock size={14} />
        {i18n.t('pending_verification')}
      </span>
    );
  };

  renderCard = (row) => {
    const isPrimary = row.is_default;

    return (
      <div
        key={row.id}
        className={`bank-account-card${isPrimary ? ' bank-account-card--primary' : ''}`}
      >
        {isPrimary && (
          <div className="bank-account-card__ribbon">
            <Star size={13} fill="#fff" />
            {i18n.t('primary')}
          </div>
        )}

        <div className="bank-account-card__body">
          <div className="bank-account-card__identity">
            <span className="bank-account-card__icon">
              <Landmark size={20} />
            </span>
            <div>
              <p className="bank-account-card__name">{row.bank_name}</p>
              <p className="bank-account-card__meta">
                {[row.branch_name, row.currency].filter(Boolean).join(' · ') || '--'}
              </p>
            </div>
          </div>

          <div className="bank-account-card__details">
            <div className="bank-account-card__row">
              <span className="bank-account-card__label">{i18n.t('account_number')}</span>
              <span className="bank-account-card__value">
                {this.maskNumber(row.account_number)}
              </span>
            </div>
            <div className="bank-account-card__row">
              <span className="bank-account-card__label">{i18n.t('iban')}</span>
              <span className="bank-account-card__value">{row.iban || '--'}</span>
            </div>
          </div>

          {this.renderVerificationBadge(row)}

          <span
            className={`bank-account-card__status bank-account-card__status--${row.is_active ? 'active' : 'inactive'}`}
            style={{ marginLeft: '0.5rem' }}
          >
            {row.is_active ? i18n.t('active') : i18n.t('inactive')}
          </span>
        </div>

        <div className="bank-account-card__footer">
          <button
            type="button"
            className="bank-account-card__action"
            onClick={() => this.handleEdit(row)}
          >
            <Pencil size={15} />
            {i18n.t('edit')}
          </button>

          {!isPrimary && (
            <button
              type="button"
              className="bank-account-card__action bank-account-card__action--primary"
              onClick={() => this.handleSetPrimaryClick(row)}
            >
              <Star size={15} />
              {i18n.t('set_as_primary')}
            </button>
          )}

          <button
            type="button"
            className="bank-account-card__action"
            onClick={() => this.handleActionClick(row)}
          >
            {row.is_active ? <PauseCircle size={15} /> : <PlayCircle size={15} />}
            {row.is_active ? i18n.t('deactivate') : i18n.t('activate')}
          </button>

          <button
            type="button"
            className="bank-account-card__action bank-account-card__action--danger"
            onClick={() => this.handleActionClick(row)}
          >
            <Trash2 size={15} />
            {i18n.t('delete')}
          </button>
        </div>
      </div>
    );
  };

  renderUnauthorized() {
    return (
      <div className="cd-page">
        <div className="cd-shell">
          <div className="cd-panel" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <ShieldAlert size={40} style={{ color: '#c2415c', marginBottom: '12px' }} />
            <h2 style={{ margin: '0 0 8px' }}>{i18n.t('access_denied') || 'Access Denied'}</h2>
            <p style={{ color: '#6b7280', margin: 0 }}>
              {i18n.t('bank_account_access_denied') ||
                'This page is only available to client accounts.'}
            </p>
          </div>
        </div>
      </div>
    );
  }
  render() {
    if (user?.role !== 'C') {
      return this.renderUnauthorized();
    }

    const {
      accounts,
      isLoading,
      showFormModal,
      selectedAccount,
      showPrimaryConfirm,
      primaryTarget,
      primaryLoading,
      showActionConfirm,
      actionTarget,
      actionLoading,
      enrollmentSource,
      enrollmentAccountId,
      paymentDestination,
      paymentAccountId,
      auctionEnrollmentConfig,
      auctionPaymentConfig,
      savedRoutingConfig,
      isConfigLoading,
      isRoutingSaving,
    } = this.state;

    const canCreate = accounts.length < 4;

    const savedEnrollmentSource = savedRoutingConfig?.enrollmentSource || 'mzadcom';
    const savedPaymentDestination = savedRoutingConfig?.paymentDestination || 'mzadcom';
    const enrollmentAccountForPreview =
      accounts.find((account) => account.id === savedRoutingConfig?.enrollmentAccountId) ||
      auctionEnrollmentConfig?.organization_bank_account ||
      null;
    const paymentAccountForPreview =
      accounts.find((account) => account.id === savedRoutingConfig?.paymentAccountId) ||
      auctionPaymentConfig?.organization_bank_account ||
      null;

    return (
      <React.Fragment>
        <div className="cd-page">
          <div className="cd-shell">
            <p className="cd-crumb">
              <NavLink to="/client-management">{i18n.t('dashboard')}</NavLink>
              <span className="cd-crumb__current">
                {i18n.t('bank_accounts') || 'Bank Accounts'}
              </span>
            </p>

            <header className="cd-hero cd-hero--compact">
              <h1 className="cd-hero__title">{i18n.t('bank_accounts')}</h1>
              <p className="cd-hero__sub">{i18n.t('bank_accounts_subtitle')}</p>
            </header>

            <div className="card">
              <div className="card-body">
                {!isLoading && (
                  <React.Fragment>
                    <SmartRoutingPreview
                      isLoading={isConfigLoading}
                      enrollmentSource={savedEnrollmentSource}
                      paymentDestination={savedPaymentDestination}
                      enrollmentAccount={enrollmentAccountForPreview}
                      paymentAccounts={paymentAccountForPreview ? [paymentAccountForPreview] : []}
                    />
                    <PaymentRoutingSettings
                      accounts={accounts}
                      enrollmentSource={enrollmentSource}
                      enrollmentAccountId={enrollmentAccountId}
                      paymentDestination={paymentDestination}
                      paymentAccountId={paymentAccountId}
                      isDirty={this.isRoutingDirty()}
                      isSaving={isRoutingSaving}
                      onEnrollmentSourceChange={this.handleEnrollmentSourceChange}
                      onEnrollmentAccountChange={this.handleEnrollmentAccountChange}
                      onPaymentDestinationChange={this.handlePaymentDestinationChange}
                      onPaymentAccountChange={this.handlePaymentAccountChange}
                      onSave={this.handleSaveRoutingChanges}
                      onDiscard={this.handleDiscardRoutingChanges}
                    />
                  </React.Fragment>
                )}

                <div className="bank-accounts">
                  <div className="bank-accounts__header">
                    <div className="bank-accounts__header-info">
                      <span className="bank-accounts__header-icon">
                        <Landmark size={22} />
                      </span>
                      <div>
                        <h2 className="bank-accounts__header-title">{i18n.t('bank_accounts')}</h2>
                        <p className="bank-accounts__header-subtitle">
                          {i18n.t('bank_accounts_subtitle')}
                        </p>
                      </div>
                    </div>

                    {canCreate && (
                      <button className="bank-accounts__add-btn" onClick={this.handleCreate}>
                        <Plus size={18} />
                        <span>{i18n.t('add_bank_account')}</span>
                      </button>
                    )}
                  </div>

                  {isLoading ? (
                    <BankAccountCardSkeleton count={4} />
                  ) : accounts.length ? (
                    <div className="bank-accounts-grid">{accounts.map(this.renderCard)}</div>
                  ) : (
                    <BankAccountsEmpty
                      message={i18n.t('no_bank_accounts_available')}
                      actionText={i18n.t('refresh')}
                      onAction={this.fetchAccounts}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create / Update modal */}
        <PaymentBankAccountModal
          show={showFormModal}
          account={selectedAccount}
          onClose={this.handleFormModalClose}
          onSaved={this.handleSaved}
        />

        {/* Set-as-primary confirmation */}
        <ConfirmModal
          show={showPrimaryConfirm}
          onClose={() => this.setState({ showPrimaryConfirm: false, primaryTarget: null })}
          title={i18n.t('set_as_primary_account')}
          titleIcon={<CheckCircle2 size={22} color="#198754" />}
          message={i18n.t('set_as_primary_confirm_message', {
            bank: primaryTarget?.bank_name,
          })}
          onConfirm={this.handleSetPrimaryConfirm}
          onCancel={() => this.setState({ showPrimaryConfirm: false, primaryTarget: null })}
          confirmText={i18n.t('confirm')}
          cancelText={i18n.t('cancel')}
          loading={primaryLoading}
        />

        {/* Activate / deactivate confirmation */}
        <ConfirmModal
          show={showActionConfirm}
          onClose={() => this.setState({ showActionConfirm: false, actionTarget: null })}
          title={
            actionTarget?.is_active ? i18n.t('deactivate_account') : i18n.t('activate_account')
          }
          titleIcon={<AlertTriangle size={22} color="#dc3545" />}
          message={
            actionTarget?.is_active
              ? i18n.t('deactivate_account_confirm_message')
              : i18n.t('activate_account_confirm_message')
          }
          onConfirm={this.handleActionConfirm}
          onCancel={() => this.setState({ showActionConfirm: false, actionTarget: null })}
          confirmText={actionTarget?.is_active ? i18n.t('deactivate') : i18n.t('activate')}
          cancelText={i18n.t('cancel')}
          loading={actionLoading}
        />
      </React.Fragment>
    );
  }
}

export default ClientBankAccount;
